import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormControlLabel, Switch, Typography } from "@mui/material";
import { Chip, PageContainer } from "@outpave/ds";
import CardProgramStatus from "enums/CardProgramStatus";
import PlatformIntegrationCategory from "enums/PlatformIntegrationCategory";
import usePlatformIntegrations, {
  PlatformIntegration,
  PlatformIntegrationTemplate,
  usePlatformIntegrationTemplates,
} from "hooks/usePlatformIntegrations";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import { integrationPath } from "utils/platformIntegrations/integrationPaths";
import { CATEGORY_LABEL_KEY } from "utils/platformIntegrations/integrationLabels";
import { Center, Columns, Stack } from "components/layout/reusableLayouts";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import IntegrationMarketplaceCard from "components/platform/integrations/IntegrationMarketplaceCard";
import IntegrationFormDrawer from "components/platform/integrations/IntegrationFormDrawer";

const CONNECTED = "CONNECTED";
const AVAILABLE = "AVAILABLE";
const CATEGORY_ORDER = Object.values(PlatformIntegrationCategory);

const isActive = (integration: PlatformIntegration) =>
  integration.status !== CardProgramStatus.INACTIVE;

/**
 * Every provider this platform runs on, for card issuing and for acquiring.
 * A provider can be connected more than once, one integration per account.
 * An integration switched off is listed only on request.
 */
const PlatformIntegrations: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breadcrumbs = useNavBreadcrumbs(t("Integrations"));
  const [tab, setTab] = useState<string | null>(null);
  // No debounce: this filters what is already loaded, not a query.
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [connecting, setConnecting] =
    useState<PlatformIntegrationTemplate | null>(null);

  const { integrations, loading, saveIntegration, checkIntegration } =
    usePlatformIntegrations();
  const { templates, getTemplate } = usePlatformIntegrationTemplates();

  const categoryLabel = (category: PlatformIntegrationCategory) =>
    t(CATEGORY_LABEL_KEY[category]);
  const providerName = (integration: PlatformIntegration) =>
    getTemplate(integration.category, integration.provider)?.label ??
    integration.provider;
  const active = useMemo(() => integrations.filter(isActive), [integrations]);
  // A platform with nothing connected yet opens on what it can connect.
  const activeTab = tab ?? (loading || active.length ? CONNECTED : AVAILABLE);
  const matches = (category: PlatformIntegrationCategory, words: string[]) => {
    const needle = query.trim().toLowerCase();
    return (
      !needle ||
      [...words, categoryLabel(category)].some((word) =>
        word.toLowerCase().includes(needle),
      )
    );
  };

  const connected = useMemo(
    () =>
      (showInactive ? integrations : active)
        .filter((integration) =>
          matches(integration.category, [
            integration.name,
            providerName(integration),
          ]),
        )
        .sort(
          (a, b) =>
            CATEGORY_ORDER.indexOf(a.category) -
              CATEGORY_ORDER.indexOf(b.category) ||
            (a.createdAt ?? 0) - (b.createdAt ?? 0),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [integrations, active, showInactive, templates, query, t],
  );
  const available = templates.filter((template) =>
    matches(template.category, [template.label]),
  );
  const countOf = (
    template: PlatformIntegrationTemplate,
    among: PlatformIntegration[] = active,
  ) =>
    among.filter(
      (integration) =>
        integration.category === template.category &&
        integration.provider === template.provider,
    ).length;

  const badges = (integration: PlatformIntegration) => {
    const chips = [
      integration.isDefault && (
        <Chip key="default" size="sm" tone="positive" label={t("Default")} />
      ),
      integration.status === CardProgramStatus.INACTIVE && (
        <Chip key="inactive" size="sm" tone="neutral" label={t("Inactive")} />
      ),
      integration.missingFields.length > 0 && (
        <Chip
          key="attention"
          size="sm"
          tone="warning"
          dot
          label={t("Needs attention")}
        />
      ),
    ].filter(Boolean);
    return chips.length ? <>{chips}</> : undefined;
  };

  // A provider already connected says so, and how many times when more than once.
  const connectedChip = (count: number) =>
    count > 0 ? (
      <Chip
        size="sm"
        tone="positive"
        dot
        label={count > 1 ? t("ConnectedCount", { count }) : t("Connected")}
      />
    ) : undefined;

  // Counts switched-off integrations too, so a new one never repeats a name.
  const defaultNameFor = (template: PlatformIntegrationTemplate) => {
    const count = countOf(template, integrations);
    return count ? `${template.label} ${count + 1}` : template.label;
  };

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      title={t("Integrations")}
      subtitle={t("PlatformIntegrationsSubtitle")}
      // The cards are their own surfaces; wrapping them in the shell's card
      // puts a surface inside the same surface.
      wrapped={false}
      tabs={{
        items: [
          { id: CONNECTED, label: t("Connected") },
          { id: AVAILABLE, label: t("Available") },
        ],
        activeId: activeTab,
        onChange: setTab,
      }}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: t("Search providers"),
      }}
      filters={
        activeTab === CONNECTED && (
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(event) => setShowInactive(event.target.checked)}
              />
            }
            label={t("Show inactive")}
          />
        )
      }
    >
      <Stack
        gap="var(--space-9)"
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          paddingBlock: "var(--space-7) var(--space-9)",
        }}
      >
        {activeTab === CONNECTED ? (
          <CategorySections
            emptyMessage={
              query ? t("NoProvidersMatch") : t("NoPlatformIntegrationsYet")
            }
            columns={{ xs: 1, sm: 2, lg: 3 }}
            items={connected.map((integration) => ({
              category: integration.category,
              node: (
                <IntegrationMarketplaceCard
                  key={integration.id}
                  domain={
                    getTemplate(integration.category, integration.provider)
                      ?.domain
                  }
                  title={integration.name}
                  subtitle={providerName(integration)}
                  badges={badges(integration)}
                  actionLabel={t("Manage")}
                  actionVariant="default-outlined"
                  onAction={() => navigate(integrationPath(integration.id))}
                />
              ),
            }))}
            categoryLabel={categoryLabel}
          />
        ) : (
          <CategorySections
            emptyMessage={t("NoProvidersMatch")}
            columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
            items={available.map((template) => ({
              category: template.category,
              node: (
                <IntegrationMarketplaceCard
                  key={`${template.category}:${template.provider}`}
                  domain={template.domain}
                  title={template.label}
                  description={t(template.descriptionKey)}
                  badges={connectedChip(countOf(template))}
                  actionLabel={
                    countOf(template) ? t("Connect another") : t("Connect")
                  }
                  actionVariant={
                    countOf(template) ? "default-outlined" : "primary-outlined"
                  }
                  onAction={() => setConnecting(template)}
                />
              ),
            }))}
            categoryLabel={categoryLabel}
          />
        )}

        <LoadingWithoutModal
          open={loading && integrations.length === 0}
          contained
        />
      </Stack>

      <IntegrationFormDrawer
        open={!!connecting}
        template={connecting}
        defaultName={connecting ? defaultNameFor(connecting) : ""}
        onClose={() => setConnecting(null)}
        onSave={saveIntegration}
        onCheck={checkIntegration}
        onSaved={(integration) => navigate(integrationPath(integration.id))}
      />
    </PageContainer>
  );
};

interface CategorySectionsProps {
  items: { category: PlatformIntegrationCategory; node: React.ReactNode }[];
  categoryLabel: (category: PlatformIntegrationCategory) => string;
  emptyMessage: string;
  columns: Record<string, number>;
}

/** One section per category that has anything to show, in category order. */
const CategorySections: React.FC<CategorySectionsProps> = ({
  items,
  categoryLabel,
  emptyMessage,
  columns,
}) => {
  if (!items.length) {
    return (
      <Center sx={{ paddingBlock: "var(--space-9)" }}>
        <Typography
          variant="body1"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {emptyMessage}
        </Typography>
      </Center>
    );
  }

  return (
    <>
      {CATEGORY_ORDER.map((category) => {
        const nodes = items
          .filter((item) => item.category === category)
          .map((item) => item.node);
        if (!nodes.length) return null;
        return (
          <Stack key={category} gap="var(--space-5)">
            <Typography variant="h5" component="h2">
              {categoryLabel(category)}
            </Typography>
            <Columns count={columns}>{nodes}</Columns>
          </Stack>
        );
      })}
    </>
  );
};

export default PlatformIntegrations;
